import React, { useEffect, useState } from 'react';
import PageFooter from '../components/chrome/PageFooter';
import { logoSrc, logoStyleChrome, websiteTermsUrl, productName } from '../brand';
import { getCurrentLegalDocument, DOC_KEY_CALCULATOR_TERMS } from '../api/legalDocuments';

function renderBodyWithWebsiteLink(body, url) {
  const marker = 'LooporaData Website Terms of Use';
  const idx = body.indexOf(marker);
  if (idx === -1) {
    return (
      <>
        {body}{' '}
        <a href={url} target="_blank" rel="noopener noreferrer">
          {marker}
        </a>
      </>
    );
  }
  return (
    <>
      {body.slice(0, idx)}
      <a href={url} target="_blank" rel="noopener noreferrer">
        {marker}
      </a>
      {body.slice(idx + marker.length)}
    </>
  );
}

function SectionBody({ section, websiteUrl }) {
  const url = websiteUrl || websiteTermsUrl;
  if (section.link_website_terms && typeof section.body === 'string') {
    return <div style={proseStyle}>{renderBodyWithWebsiteLink(section.body, url)}</div>;
  }
  if (typeof section.body === 'string') {
    return <div style={proseStyle}>{section.body}</div>;
  }
  return <div style={proseStyle}>{section.body}</div>;
}

export default function CalculatorTerms({ onBack, showSignIn }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCurrentLegalDocument(DOC_KEY_CALCULATOR_TERMS)
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.code || err?.message || 'legal_document_unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={pageStyle} data-testid="calculator-terms-page">
      <header style={headerStyle}>
        <button type="button" onClick={onBack} style={logoBtnStyle} aria-label="Back">
          <img src={logoSrc} alt={productName} style={logoStyleChrome} />
        </button>
        {showSignIn && (
          <button type="button" onClick={showSignIn} style={signInStyle}>
            Sign in
          </button>
        )}
      </header>
      <main style={mainStyle}>
        {error && (
          <p style={errorStyle} role="alert">
            Terms are temporarily unavailable. Try again later.
          </p>
        )}
        {!error && !doc && <p style={ledeStyle}>Loading terms…</p>}
        {doc && (
          <>
            <h1 style={titleStyle}>{doc.title}</h1>
            {doc.sections.map((section) => (
              <section key={section.title} style={sectionStyle}>
                <h2 style={sectionTitleStyle}>{section.title}</h2>
                <SectionBody section={section} websiteUrl={websiteTermsUrl} />
              </section>
            ))}
          </>
        )}
      </main>
      {doc && <PageFooter legalDocumentVersion={doc.version} />}
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  fontFamily: 'var(--font)',
};

const headerStyle = {
  height: '76px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 56px',
  borderBottom: '1px solid var(--line)',
  gap: '24px',
};

const logoBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const signInStyle = {
  marginLeft: 'auto',
  background: 'transparent',
  border: 'none',
  color: 'var(--ink2)',
  fontSize: '13.5px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};

const mainStyle = {
  flex: 1,
  maxWidth: '720px',
  margin: '0 auto',
  padding: '40px 24px 48px',
  width: '100%',
  boxSizing: 'border-box',
};

const titleStyle = {
  fontFamily: 'var(--display)',
  fontSize: '28px',
  fontWeight: 500,
  margin: 0,
  color: 'var(--ink)',
  lineHeight: 1.25,
};

const ledeStyle = {
  fontSize: '14px',
  color: 'var(--ink2)',
  lineHeight: 1.55,
  margin: '12px 0 28px',
};

const errorStyle = {
  ...ledeStyle,
  color: 'var(--danger, #cf1322)',
};

const sectionStyle = { marginBottom: '28px', marginTop: '28px' };

const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: 600,
  margin: '0 0 8px',
  color: 'var(--ink)',
};

const proseStyle = {
  fontSize: '14px',
  color: 'var(--ink2)',
  lineHeight: 1.65,
  margin: 0,
};
